import type { MotivoColisao } from '../tipos';
import { palavrasEscritas } from '../modos/corre';
import { faseDoNivel, tituloDaFase } from '../modos/palavredo';
import type { EstadoJogo } from './estados';

/**
 * Mensagens de feedback (§7.3).
 *
 * As strings estão centralizadas aqui porque são texto de aceite: os testes de
 * T02-06, T05-01 e T08-01 comparam com estas constantes, não com literais
 * espalhados pela interface. Nenhuma mensagem depende só de cor (§7.3, §13).
 */

export const MSG_CONTINUE = 'Continue digitando';
export const MSG_RECONHECIDA = 'Palavra reconhecida — escolha um cruzamento';
export const MSG_SEM_CRUZAMENTO = 'Palavra válida, mas sem cruzamentos disponíveis';
/** R5-15: nenhuma palavra se repete no tabuleiro, em modo nenhum. */
export const MSG_REPETIDA = 'Essa palavra já está no tabuleiro';
export const MSG_EDITE = 'Edite a palavra para continuar';
export const MSG_ESCOLHA_LETRA = 'Escolha uma letra para continuar';
/** T05-05: o draft contém a âncora, mas nenhuma ocorrência cabe no tabuleiro. */
export const MSG_NAO_CABE = 'Essa palavra não cabe aqui — tente outra';
export const MSG_SESSAO_RESTAURADA = 'Sessão restaurada';
export const MSG_FALHA_SALVAR = 'Não foi possível salvar neste dispositivo';
export const MSG_FALHA_DICIONARIO = 'Não foi possível carregar as palavras';
export const MSG_CARREGANDO = 'Carregando as palavras';
export const MSG_COMECE = 'Entre uma palavra';

/**
 * Nome de cada modo na interface (§22).
 *
 * **Atenção:** o nome de tela e o identificador interno deixaram de coincidir.
 * Os identificadores (`termo`, `zen`, `corre`, `trama`) são o vocabulário do
 * núcleo, da persistência e dos critérios da spec, e renomeá-los quebraria
 * toda sessão já salva no aparelho de quem joga. Os nomes de tela mudaram
 * sozinhos, e a coincidência infeliz é que `Trama` agora é o nome de `termo`,
 * enquanto o identificador `trama` se chama `Átrio`. Ao ler este arquivo, o
 * que manda é a chave; ao ler a tela, o valor.
 */
export const NOME_MODO = {
  termo: 'Trama',
  zen: 'Bistrô',
  corre: 'Nitro',
  trama: 'Átrio',
  // O único cujo nome de tela é o próprio identificador: nasceu depois da
  // confusão descrita acima, e não tinha por que repeti-la.
  palavredo: 'Palavredo',
} as const;

/** §23.1: o resultado da Trama é este, e não um número. */
export const MSG_ACHOU = 'Palavra do dia encontrada';

/** §22.2: antes da primeira letra o cronômetro está parado, e isso se diz. */
export const MSG_CORRIDA_PARADA = 'Digite para o cronômetro começar';

/**
 * O placar é o resultado, então a linha de feedback o repete — cada modo com a
 * unidade que ele de fato cobra (§22.2, §22.4, §23.1).
 */
export function msgFimDaPartida(estado: EstadoJogo): string {
  // §23.1: a Trama não tem placar. O resultado dela é ter achado a palavra, e
  // contar quantas foram precisas transformaria uma dedução em corrida — quem
  // escreve uma palavra a mais para experimentar uma letra estaria perdendo.
  if (estado.modo === 'termo') return MSG_ACHOU;
  if (estado.modo === 'corre') {
    const palavras = palavrasEscritas(estado.cadeia);
    return `Fim do tempo — ${palavras} ${palavras === 1 ? 'palavra' : 'palavras'}`;
  }
  // §22.7: no arcade `fim` não é fim de partida, é a pausa entre fases — e o
  // modo não tem placar. Dizer "fim da partida — 14 pontos" aqui inventava as
  // duas coisas de uma vez.
  if (estado.modo === 'palavredo') return tituloDaFase(faseDoNivel(estado.nivel ?? 1));
  return `Fim da partida — ${estado.pontos} ${estado.pontos === 1 ? 'ponto' : 'pontos'}`;
}

/** Pontos ganhos na jogada que acabou de ser confirmada (§22.3). */
export function anunciarPontos(pontos: number, extras: number, multiplicador: number): string {
  const partes = [`${pontos} ${pontos === 1 ? 'ponto' : 'pontos'}`];
  if (extras > 0) partes.push(`${extras} ${extras === 1 ? 'travessia' : 'travessias'} extra`);
  if (multiplicador > 1) partes.push(`multiplicador ${multiplicador}`);
  return partes.join(', ');
}

/**
 * Por que a palavra não coube (§7.3, T05-05).
 *
 * "Não cabe" sozinho manda o jogador adivinhar entre quatro coisas diferentes:
 * a letra bate noutra letra, a vertical que se formaria não existe, a palavra
 * encostaria em outra, ou passaria por cima de uma. Cada uma pede uma correção
 * diferente, então cada uma tem a sua frase.
 *
 * Todas começam em minúscula: entram depois de um `·` na mesma linha, como
 * continuação da mensagem, e não como frase nova.
 */
export function explicarColisao(motivo: MotivoColisao, formada?: string): string {
  switch (motivo) {
    case 'letra-diferente-no-cruzamento':
      return 'uma das letras cairia sobre outra diferente';
    case 'perpendicular-invalida':
      return formada === undefined
        ? 'formaria na vertical uma sequência que não é palavra'
        : `formaria ${formada.toUpperCase()} na vertical, que não é palavra`;
    case 'sem-respiro':
      return 'ficaria colada em outra palavra da mesma linha';
    case 'sobreposicao-paralela':
      return 'passaria por cima de uma palavra já escrita';
    case 'cruzamento-fora-de-lugar':
      return 'a letra escolhida não cairia sobre a âncora';
  }
}

/** `Inclua a letra V` (§7.3, T05-01). */
export function msgIncluaLetra(letra: string): string {
  return `Inclua a letra ${letra.toUpperCase()}`;
}

/** Anúncio de sugestão para leitor de tela (§13). */
export function anunciarSugestao(texto: string, letra: string, posicao: number): string {
  return `${texto}, cruzando na letra ${letra.toUpperCase()}, posição ${posicao + 1}`;
}

export function anunciarCaracteresRecusados(recusados: readonly string[]): string {
  if (recusados.length === 0) return '';
  const unicos = [...new Set(recusados)].join(' ');
  return `Caractere não permitido: ${unicos}`;
}

/** O draft contém a âncora, mas nenhuma ocorrência dela cabe (T05-05). */
export function naoCabe(estado: EstadoJogo): boolean {
  if (estado.fase !== 'draft-ancorado' || estado.ancora === undefined) return false;
  return estado.encaixes.length > 0 && !estado.encaixes.some((encaixe) => encaixe.valido);
}

/**
 * Segunda frase da linha de feedback, quando a primeira não basta (§7.3).
 *
 * Sai daqui, e não da interface, porque é texto de aceite como o resto de §7.3.
 */
export function complementoDoEstado(estado: EstadoJogo): string | undefined {
  if (estado.fase === 'sem-cruzamento' || estado.fase === 'repetida') return MSG_EDITE;
  if (!naoCabe(estado)) return undefined;

  // Os encaixes vêm ordenados: sem nenhum válido, o primeiro é o que mais
  // perto chegou de caber, e é sobre ele que vale explicar.
  const barrado = estado.encaixes.find((encaixe) => encaixe.motivo !== undefined);
  if (barrado?.motivo === undefined) return undefined;
  return explicarColisao(barrado.motivo, barrado.formada);
}

/** A linha única de feedback exibida junto ao draft (§7.3). */
export function mensagemDoEstado(estado: EstadoJogo): string {
  switch (estado.fase) {
    case 'carregando':
      return estado.falhaDicionario ? MSG_FALHA_DICIONARIO : MSG_CARREGANDO;
    case 'inicio':
      return MSG_COMECE;
    case 'digitando':
      return MSG_CONTINUE;
    case 'palavra-valida':
      return MSG_RECONHECIDA;
    case 'sem-cruzamento':
      return MSG_SEM_CRUZAMENTO;
    case 'repetida':
      return MSG_REPETIDA;
    case 'escolher-ancora':
      return MSG_ESCOLHA_LETRA;
    case 'fim':
      return msgFimDaPartida(estado);
    case 'draft-ancorado': {
      if (estado.ancora === undefined) return MSG_CONTINUE;
      // T05-05: dizer "inclua a letra V" para quem já digitou um V é mentir
      // sobre o motivo. Se há ocorrências e nenhuma cabe, o problema é espaço.
      if (naoCabe(estado)) return MSG_NAO_CABE;
      return msgIncluaLetra(estado.ancora.caractere);
    }
  }
}
